import { CONVEYANCE_TYPES } from '@koala/sdk/v4';
import { readableColor } from 'polished';
import styled from 'styled-components';
import { Stack } from '@/components/ui';
import { useLocation } from '@/features/locations/service';
import { LOCATION_PILL_COPY } from '@/features/location-pill/constants';
import { LocationPicker } from '@/features/location-pill/location-picker';
import { LOCATION_PILL_CLASS } from '@/features/location-pill/location-pill';
import { useSelector } from '@/redux';
import { useConfigOverride } from '@/redux/cmsConfig';
import { selectConveyance } from '@/redux/conveyanceMode/reducer';
import { safePolished } from '@/utils/safePolished';

type Conveyance = ReturnType<typeof selectConveyance>;

/**
 * The header's "ordering from" indicator: the Location Pill for the diner's store,
 * plus "to <address>" for a delivery order (TEAM-13315). The store is the basket's
 * store, falling back to the menu page's store.
 */
export function Destination() {
  const { location, type, address } = useSelector(selectConveyance);
  const { data, isSuccess } = useLocation(location);
  // Same default as the header itself (`header.tsx`): no configured colour means white.
  const { background_color } = useConfigOverride('header');
  const lightHeader = safePolished(readableColor)(background_color ?? 'white') === '#000';

  if (!isSuccess || !data?.data) {
    return null;
  }

  const deliveryAddress = getDeliveryLine(type, address);

  return (
    <StyledStack gap="$3" $hasDeliveryLine={Boolean(deliveryAddress)}>
      <LocationPicker location={data.data} conveyanceType={type} accentPin={lightHeader} />

      {deliveryAddress && (
        <DeliveryTo>
          <Origin>{LOCATION_PILL_COPY.deliveryTo}</Origin>
          <Label>{deliveryAddress}</Label>
        </DeliveryTo>
      )}
    </StyledStack>
  );
}

/**
 * The "to <address>" text for a delivery order with an address, otherwise `null`.
 * The header sizes its mobile menu-availability row from this, so both stay in step.
 */
export function getDeliveryLine(
  type: Conveyance['type'],
  address: Conveyance['address'],
): string | null {
  if (type !== CONVEYANCE_TYPES.DELIVERY || !address?.street_address) {
    return null;
  }

  const streetAddress = address.street_address_2
    ? `${address.street_address} ${address.street_address_2}`
    : address.street_address;

  return `${streetAddress}, ${address.city}`;
}

const StyledStack = styled(Stack)<{ $hasDeliveryLine: boolean }>(({ $hasDeliveryLine }) => ({
  maxWidth: '100%',
  minWidth: 0,
  overflow: 'hidden',

  // With an address beside it, the pill keeps its natural width but always leaves 6rem
  // (plus the gap) for "to <address>"; on its own the pill shrinks and truncates as usual.
  ...($hasDeliveryLine && {
    [`& > .${LOCATION_PILL_CLASS}`]: {
      flex: '0 0 auto',
      maxWidth: 'calc(100% - 6.75rem)',
    },
  }),

  // Mobile: full-width pill on its own row, "to <address>" under it (TEAM-13315 mobile frames).
  '@media (max-width: 767px)': {
    alignItems: 'stretch',
    flexDirection: 'column',
    gap: '4px',
    width: '100%',

    [`& > .${LOCATION_PILL_CLASS}`]: {
      maxWidth: '100%',
    },
  },
}));

const DeliveryTo = styled.span({
  display: 'flex',
  flex: '0 1 auto',
  gap: '6px',
  minWidth: 0,
  '@media (max-width: 375px)': {
    fontSize: '12px',
  },
});

const Origin = styled.span({
  opacity: 0.5,
});

const Label = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
